import { FC, useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { useHotkey } from "@tanstack/react-hotkeys";
import styled from "@emotion/styled";
import { useMutation, useQuery } from "convex/react";
import { toast } from "react-toastify";
import { api, Id } from "@/smart-views/client/api";
import { isConvexConfigured } from "@/smart-views/client/convex-client";
import { getSmartViewUpdateToastId } from "@/smart-views/client/update-notifications";
import { SmartViewDashboard } from "@/smart-views/components/SmartViewDashboard";
import { SmartViewDebugOverlay } from "@/smart-views/components/SmartViewDebugOverlay";
import { SmartViewHeader } from "@/smart-views/components/SmartViewHeader";
import { SmartViewVersionPanel } from "@/smart-views/components/SmartViewVersionPanel";
import { PreviewBanner } from "@/smart-views/components/PreviewBanner";
import { SMART_VIEW_ICON_KIND } from "@/smart-views/icons";
import { MDSBanner, MDSBannerKind, MDSText, MDSTextColor, MDSTextVariant } from "@/mds";

type VisibleVersionUpdate = {
  versionId: Id<"smartViewVersions">;
  versionNumber: number;
  summary: string;
  changedBlockIds: Id<"blocks">[];
};

type SmartViewUpdateNotificationLike = {
  summary: string;
  changedBlockIds: Id<"blocks">[];
  updates?: VisibleVersionUpdate[];
};

export const SmartViewPage: FC = () => {
  const { smartViewId: rawId } = useParams<{ smartViewId: string }>();
  const smartViewId = rawId;
  const [debugOpen, setDebugOpen] = useState(false);
  const [versionPanelOpen, setVersionPanelOpen] = useState(false);
  const [previewVersionId, setPreviewVersionId] = useState<Id<"smartViewVersions"> | null>(null);
  const [dismissedUpdateVersionId, setDismissedUpdateVersionId] =
    useState<Id<"smartViewVersions"> | null>(null);
  const [visibleUpdateNotification, setVisibleUpdateNotification] = useState<{
    versionId: Id<"smartViewVersions">;
    updates: VisibleVersionUpdate[];
    changedBlockIds: Id<"blocks">[];
  } | null>(null);
  const lastMarkedVersionIdRef = useRef<Id<"smartViewVersions"> | null>(null);
  const isDocumentVisible = useDocumentVisible();

  useHotkey({ key: "d", meta: true, shift: true }, () => setDebugOpen(o => !o), {
    target: window,
    preventDefault: true,
  });

  const data = useQuery(
    api.smartViews.client.getSmartView,
    smartViewId && isConvexConfigured()
      ? { smartViewId, versionId: previewVersionId ?? undefined }
      : "skip"
  );

  const restoreVersion = useMutation(api.smartViews.client.restoreVersion);
  const markVersionSeen = useMutation(api.smartViews.client.markVersionSeen);

  useEffect(() => {
    if (!smartViewId || !data?.currentVersion || data.isPreviewingPastVersion) {
      return;
    }

    if (!isDocumentVisible) {
      return;
    }

    const currentVersionId = data.currentVersion._id;
    const previousVisibleVersionId = lastMarkedVersionIdRef.current;

    if (data.updateNotification) {
      toast.dismiss(getSmartViewUpdateToastId(smartViewId));

      if (
        previousVisibleVersionId === null &&
        dismissedUpdateVersionId !== currentVersionId &&
        visibleUpdateNotification?.versionId !== currentVersionId
      ) {
        const visibleUpdates = getVisibleUpdatesForNotification({
          notification: data.updateNotification,
          currentVersion: data.currentVersion,
        });

        setVisibleUpdateNotification({
          versionId: currentVersionId,
          updates: visibleUpdates,
          changedBlockIds: getChangedBlockIdsForUpdates(visibleUpdates),
        });
      } else if (previousVisibleVersionId !== null) {
        setVisibleUpdateNotification(null);
      }
    }

    if (lastMarkedVersionIdRef.current === currentVersionId) {
      return;
    }

    lastMarkedVersionIdRef.current = currentVersionId;
    void markVersionSeen({ smartViewId, versionId: currentVersionId });
  }, [
    data,
    dismissedUpdateVersionId,
    isDocumentVisible,
    markVersionSeen,
    smartViewId,
    visibleUpdateNotification?.versionId,
  ]);

  if (!isConvexConfigured()) {
    return <MessageWrapper>Smart Views are temporarily unavailable.</MessageWrapper>;
  }

  if (!smartViewId) return null;
  if (data === undefined) return <MessageWrapper>Loading…</MessageWrapper>;
  if (data === null) return <MessageWrapper>Smart View not found.</MessageWrapper>;

  const isPreviewing = data.isPreviewingPastVersion;

  const onRestore = async () => {
    if (!previewVersionId) return;
    await restoreVersion({ smartViewId, versionId: previewVersionId });
    setPreviewVersionId(null);
  };

  return (
    <Wrapper>
      <Main>
        {isPreviewing && data.currentVersion && (
          <PreviewBanner
            versionNumber={data.currentVersion.versionNumber}
            onRestore={onRestore}
            onExit={() => setPreviewVersionId(null)}
          />
        )}
        <SmartViewHeader
          view={
            isPreviewing && data.currentVersion
              ? {
                  ...data.view,
                  title: data.currentVersion.title ?? data.view.title,
                  description: data.currentVersion.description ?? data.view.description,
                }
              : data.view
          }
          backgroundState={data.backgroundState}
          isPreviewing={isPreviewing}
          onOpenVersionHistory={() => setVersionPanelOpen(true)}
        />
        {!isPreviewing && visibleUpdateNotification && (
          <UpdateBannerList>
            {visibleUpdateNotification.updates.map(update => (
              <UpdateBanner
                key={update.versionId}
                kind={MDSBannerKind.Info}
                iconKind={SMART_VIEW_ICON_KIND}
                message={update.summary}
                onDismiss={() => {
                  setVisibleUpdateNotification(current => {
                    if (!current) {
                      return null;
                    }

                    const remainingUpdates = current.updates.filter(
                      candidate => candidate.versionId !== update.versionId
                    );

                    if (remainingUpdates.length === 0) {
                      setDismissedUpdateVersionId(current.versionId);
                      return null;
                    }

                    return {
                      ...current,
                      updates: remainingUpdates,
                      changedBlockIds: getChangedBlockIdsForUpdates(remainingUpdates),
                    };
                  });
                }}
              />
            ))}
          </UpdateBannerList>
        )}
        <SmartViewDashboard
          smartViewId={smartViewId}
          blocks={data.blocks}
          blockVersions={data.blockVersions}
          workItems={data.workItems}
          layout={data.currentVersion?.layout ?? []}
          isReadOnly={isPreviewing}
          changedBlockIds={visibleUpdateNotification?.changedBlockIds ?? []}
        />
      </Main>
      {versionPanelOpen && (
        <SmartViewVersionPanel
          smartViewId={smartViewId}
          currentVersionId={data.view.currentVersionId}
          previewVersionId={previewVersionId}
          onPreview={setPreviewVersionId}
          onClose={() => setVersionPanelOpen(false)}
        />
      )}
      {debugOpen && (
        <SmartViewDebugOverlay smartViewId={smartViewId} onClose={() => setDebugOpen(false)} />
      )}
    </Wrapper>
  );
};

const MessageWrapper: FC<{ children: React.ReactNode }> = ({ children }) => (
  <Wrapper>
    <Main>
      <Centered>
        <MDSText variant={MDSTextVariant.ParagraphSm} color={MDSTextColor.Tertiary}>
          {children}
        </MDSText>
      </Centered>
    </Main>
  </Wrapper>
);

const Wrapper = styled.div`
  display: flex;
  height: 100%;
  width: 100%;
  min-height: 0;
`;

const Main = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  min-width: 0;
  overflow: auto;
`;

const Centered = styled.div`
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: ${({ theme }) => theme.spacing["8"]};
`;

const UpdateBannerList = styled.div`
  display: flex;
  flex-direction: column;
  flex-shrink: 0;
`;

const UpdateBanner = styled(MDSBanner)`
  flex-shrink: 0;
`;

const getChangedBlockIdsForUpdates = (updates: VisibleVersionUpdate[]): Id<"blocks">[] => {
  return Array.from(new Set(updates.flatMap(update => update.changedBlockIds)));
};

const getVisibleUpdatesForNotification = ({
  notification,
  currentVersion,
}: {
  notification: SmartViewUpdateNotificationLike;
  currentVersion: { _id: Id<"smartViewVersions">; versionNumber: number };
}): VisibleVersionUpdate[] => {
  if (notification.updates && notification.updates.length > 0) {
    return notification.updates;
  }

  return [
    {
      versionId: currentVersion._id,
      versionNumber: currentVersion.versionNumber,
      summary: notification.summary,
      changedBlockIds: notification.changedBlockIds,
    },
  ];
};

const useDocumentVisible = (): boolean => {
  const [isVisible, setIsVisible] = useState(() => document.visibilityState === "visible");

  useEffect(() => {
    const handleVisibilityChange = () => {
      setIsVisible(document.visibilityState === "visible");
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => document.removeEventListener("visibilitychange", handleVisibilityChange);
  }, []);

  return isVisible;
};
